import { Component, computed, EventEmitter, inject, Output, signal, WritableSignal } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';

import { ButtonComponent } from '../../../../../shared/button/button.component';
import { ItemMenuComponent } from "../item-menu/item-menu.component";

import { PermissionService } from '../../../../services/common/permission.service';
import { EventsService } from '../../../../services/common/events.service';
import { CnpjService } from '../../../../services/common/cnpj.service';
import { UserService } from '../../../../services/common/user.service';
import { MenuService } from '../../services/menu.service';
import { MenuFavoritesService } from '../../services/menu-favorites.service';

import { FormatCnpjPipe } from "../../../../pipes/format-cnpj.pipe";

import { UserStore } from '../../models/internal/user.store';
import { ItemMenu } from '../../models/internal/item-menu-model';
import { chaveTool } from '../../models/internal/tool-model';

import { APP_VERSION } from '../../../../constants/app-version.constants';

import { PermissionBaseComponent } from '../../../../abstracts/permission-base.component';

@Component({
  selector: 'app-menu-header',
  imports: [
    RouterModule,
    ItemMenuComponent,
    FormatCnpjPipe,
    ButtonComponent,
    MatButtonModule,
    MatListModule,
    MatIconModule
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  animations: [
    trigger('rotateArrow', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition('expanded  <=> collapsed', animate('300ms ease-out'))
    ])
  ]
})
export class MenuComponent extends PermissionBaseComponent {
  @Output() toggleMenu = new EventEmitter<void>()

  public menuService: MenuService = inject(MenuService);
  public userService: UserService = inject(UserService);
  public cnpjService: CnpjService = inject(CnpjService);
  public userStore: UserStore = inject(UserStore);

  public readonly appVersion = APP_VERSION;

  public menuItems: ItemMenu[] = [];

  private readonly favoritesService = inject(MenuFavoritesService);

  /**
   * Os favoritos são resolvidos contra o menu vivo, e não guardados como objeto: um item
   * que o usuário deixou de poder acessar some do array e portanto não é encontrado
   * aqui. A permissão vence a preferência sem precisar de código para isso.
   *
   * Subitem favoritado herda o ícone do pai — SubMenu não tem ícone próprio, e sem ele
   * a linha ficaria torta no meio dos outros favoritos.
   */
  public favoritos = computed<ItemMenu[]>(() => {
    const folhas: ItemMenu[] = this.menuItems.flatMap(item =>
      item.submenu?.length
        ? item.submenu.map(sub => ({ ...sub, icon: item.icon }))
        : [item]
    );

    return this.favoritesService.favoritos()
      .map(chave => folhas.find(f => chaveTool(f) === chave))
      .filter((f): f is ItemMenu => !!f);
  });

  public userMenu: WritableSignal<boolean> = signal(false);

  public isLoadingBeLicensed: WritableSignal<boolean> = signal<boolean>(false);
  public isSuccessBeLicensed: WritableSignal<boolean> = signal<boolean>(false);

  constructor(
    override permissionService: PermissionService,
    private eventService: EventsService,
    private router: Router
  ) {
    super(permissionService);
    this.menuItems = this.menuService.getItemMenu();
  }

  public applyForLicense(): void {
    this.isLoadingBeLicensed.set(true);
    this.eventService.eventTrack('CLICK_QUERO_SER_LICENCIADO');
    this.isLoadingBeLicensed.set(false);
    this.isSuccessBeLicensed.set(true);
    this.router.navigate(['/termos']);
    this.toggleMenu.emit();
  }

  public logout(): void {
    this.userService.logout();
  }

  public interactMenu(): void {
    this.toggleMenu.emit();
  }

  public closeUserMenu() {
    this.userMenu.set(!this.userMenu());
  }

  public onItemClick(event: { title: string; link?: string; externalLink?: string }): void {
    // Trackear o evento de clique com o EventsService
    const eventName = `MENU_CLICK_${event.title.toUpperCase().replace(/\s+/g, '_')}`;
    this.eventService.eventTrack(eventName, {
      title: event.title,
      link: event.link,
      externalLink: event.externalLink
    });

    console.log('Item do menu clicado:', event);
  }
}